/**
 * The structural annotation layer — docs/01-architecture.md §3.1.
 *
 * Terrain has two layers. The cell grid is the truth; this is the *map*. It is
 * a coarse per-8x8-block tag written once by worldgen and never invalidated by
 * cell changes, which is the entire point: pathfinding, camera framing and the
 * minimap read this, so blowing a hole in a wall does not turn navigation into
 * noise. A destroyed SHELL block is still labelled SHELL — the room it bounded
 * still exists as a place, even if you can now walk through its side.
 *
 * Block granularity (8x8) is chosen so a 128-cell chunk maps to exactly a
 * 16x16 block region — one flat Uint8Array per chunk, indexable by shift.
 *
 * DECOR is deliberately id 0 so a freshly-allocated (but not yet written)
 * region reads as impassable filler. A default that flood-fills as *open*
 * would silently turn a generation bug into a false connectivity pass.
 */

import { CHUNK_SHIFT, CHUNK_SIZE } from '../sim/world';

export const BLOCK_SHIFT = 3;
export const BLOCK_SIZE = 1 << BLOCK_SHIFT; // 8 cells
export const BLOCK_MASK = BLOCK_SIZE - 1;

/** log2(blocks per chunk axis) = 7 - 3 = 4, i.e. 16x16 blocks per chunk. */
export const CHUNK_BLOCK_SHIFT = CHUNK_SHIFT - BLOCK_SHIFT;
export const BLOCKS_PER_CHUNK = CHUNK_SIZE >> BLOCK_SHIFT; // 16
export const BLOCKS_PER_CHUNK_MASK = BLOCKS_PER_CHUNK - 1;
export const BLOCKS_PER_REGION = BLOCKS_PER_CHUNK * BLOCKS_PER_CHUNK; // 256

export const Struct = {
  /** Solid filler terrain. Not a place, just the stuff between places. */
  DECOR: 0,
  /** Open space with no authored role — natural cave volume. */
  EMPTY: 1,
  /** Open space with a floor under it. Enemies path here, camera frames here. */
  WALKABLE_FLOOR: 2,
  /** The generator's guaranteed spine. Connectivity is verified over these. */
  CRITICAL_PATH: 3,
  /** Room boundary. Destructible, but the path never depends on it. */
  SHELL: 4,
  /** ADAMANT_SEAL. Opens only to the matching traversal ability. */
  GATE: 5,
} as const;

export type StructTag = (typeof Struct)[keyof typeof Struct];

export const STRUCT_NAMES = [
  'DECOR',
  'EMPTY',
  'WALKABLE_FLOOR',
  'CRITICAL_PATH',
  'SHELL',
  'GATE',
] as const;

/**
 * Blocks the connectivity fill may cross. §3.4: "using only WALKABLE_FLOOR /
 * empty space, assuming *no* destruction". GATE is excluded on purpose — a
 * route that needs an ability is not a route the verifier is allowed to count,
 * otherwise gating stops being honest.
 */
export function isPassable(tag: number): boolean {
  return tag === Struct.EMPTY || tag === Struct.WALKABLE_FLOOR || tag === Struct.CRITICAL_PATH;
}

/**
 * What the map is allowed to say about a chunk — issue #25.
 *
 * The map rendered every room as `normal` because worldgen tagged nothing, and
 * a map that cannot show you where the checkpoint is does not help you decide
 * where to go, which is the only reason to open one.
 *
 * This list is deliberately short, and it is a closed set for a reason the
 * issue is blunt about: **do not invent kinds the world does not have.** A map
 * showing a boss room that is not there is worse than no map, because the
 * player navigates by it. There is no `boss` here because worldgen does not
 * place bosses (`game.ts` does, at a run-time position nothing records), and no
 * `shop` because factions (#11) do not exist yet. When they do, they add a
 * member here and a case in `WorldGen.roomKindAt` together, or not at all.
 */
export type RoomKind = 'normal' | 'surface' | 'sanctum' | 'rest';
export const ROOM_KINDS: readonly RoomKind[] = ['normal', 'surface', 'sanctum', 'rest'];

/** A point on the critical path that generation promises stays reachable. */
export interface Anchor {
  x: number;
  y: number;
  biome: string;
}

export interface ConnectivityResult {
  ok: boolean;
  unreachable: Anchor[];
  /** Blocks visited by the fill — useful for eyeballing coverage in tests. */
  visited: number;
}

export interface RedundancyResult {
  ok: boolean;
  /** Number of anchor pairs examined. */
  checked: number;
  weak: { a: Anchor; b: Anchor; reason: string }[];
}

interface BlockGraph {
  n: number;
  /** 4 slots per vertex, -1 padded. */
  adj: Int32Array;
  index: Map<number, number>;
}

interface BccLabels {
  /** Up to 4 component ids per vertex (degree <= 4), -1 padded. */
  labels: Int32Array;
  /** Vertex count per component. A 2-vertex component is a bridge. */
  size: number[];
}

/** ±32767 chunks per axis, same packing convention as CellWorld. */
function regionKey(cx: number, cy: number): number {
  return ((cx + 32768) << 16) | (cy + 32768);
}

/**
 * Block keys must survive the full ±(32768*16) block range, which overflows
 * 32-bit packing — so use a float-safe multiply instead of shifts. 2^19 * 2^20
 * is well inside Number.MAX_SAFE_INTEGER.
 */
const BLOCK_KEY_BIAS = 0x80000;
const BLOCK_KEY_STRIDE = 0x100000;
function blockKey(bx: number, by: number): number {
  return (bx + BLOCK_KEY_BIAS) * BLOCK_KEY_STRIDE + (by + BLOCK_KEY_BIAS);
}

export class StructureMap {
  private regions = new Map<number, Uint8Array>();

  /** Single-entry cache. Worldgen writes 256 consecutive blocks per chunk. */
  private cacheKey = -1;
  private cacheRegion: Uint8Array | null = null;

  get regionCount(): number {
    return this.regions.size;
  }

  /** Region array for a chunk, allocating on first touch. */
  region(cx: number, cy: number): Uint8Array {
    const key = regionKey(cx, cy);
    if (key === this.cacheKey && this.cacheRegion !== null) return this.cacheRegion;
    let r = this.regions.get(key);
    if (r === undefined) {
      r = new Uint8Array(BLOCKS_PER_REGION); // zero-filled = DECOR
      this.regions.set(key, r);
    }
    this.cacheKey = key;
    this.cacheRegion = r;
    return r;
  }

  /** Region array if it exists, else null. Never allocates. */
  peekRegion(cx: number, cy: number): Uint8Array | null {
    const key = regionKey(cx, cy);
    if (key === this.cacheKey) return this.cacheRegion;
    const r = this.regions.get(key) ?? null;
    this.cacheKey = key;
    this.cacheRegion = r;
    return r;
  }

  hasRegion(cx: number, cy: number): boolean {
    return this.regions.has(regionKey(cx, cy));
  }

  // ------------------------------------------------------- block accessors

  /** Tag at block coords, or -1 for ungenerated space. */
  tagAtBlock(bx: number, by: number): number {
    const r = this.peekRegion(bx >> CHUNK_BLOCK_SHIFT, by >> CHUNK_BLOCK_SHIFT);
    if (r === null) return -1;
    return r[((by & BLOCKS_PER_CHUNK_MASK) << CHUNK_BLOCK_SHIFT) | (bx & BLOCKS_PER_CHUNK_MASK)];
  }

  setBlock(bx: number, by: number, tag: StructTag | number): void {
    const r = this.region(bx >> CHUNK_BLOCK_SHIFT, by >> CHUNK_BLOCK_SHIFT);
    r[((by & BLOCKS_PER_CHUNK_MASK) << CHUNK_BLOCK_SHIFT) | (bx & BLOCKS_PER_CHUNK_MASK)] = tag;
  }

  // ------------------------------------------------------- world accessors

  /** Tag covering a world *cell*, or -1 for ungenerated space. */
  get(worldX: number, worldY: number): number {
    return this.tagAtBlock(worldX >> BLOCK_SHIFT, worldY >> BLOCK_SHIFT);
  }

  set(worldX: number, worldY: number, tag: StructTag | number): void {
    this.setBlock(worldX >> BLOCK_SHIFT, worldY >> BLOCK_SHIFT, tag);
  }

  /** Stamp a world-space rect. Coordinates are cells; granularity is blocks. */
  setRect(x: number, y: number, w: number, h: number, tag: StructTag | number): void {
    const b0x = x >> BLOCK_SHIFT;
    const b0y = y >> BLOCK_SHIFT;
    const b1x = (x + w - 1) >> BLOCK_SHIFT;
    const b1y = (y + h - 1) >> BLOCK_SHIFT;
    for (let by = b0y; by <= b1y; by++) {
      for (let bx = b0x; bx <= b1x; bx++) this.setBlock(bx, by, tag);
    }
  }

  /** True if a world cell sits in a block the player can route through. */
  passableAt(worldX: number, worldY: number): boolean {
    return isPassable(this.get(worldX, worldY));
  }

  // -------------------------------------------------------- §3.4 verifier

  /**
   * Flood fill from the first reachable anchor over passable blocks and report
   * anchors the fill never touched. Ungenerated space (-1) is a wall: the
   * verifier only ever makes claims about the region that actually exists.
   *
   * This runs at generation time, not per frame, so a Set/queue of numbers is
   * the right trade — clarity over allocation.
   */
  verifyConnectivity(anchors: readonly Anchor[]): ConnectivityResult {
    const unreachable: Anchor[] = [];
    if (anchors.length === 0) return { ok: true, unreachable, visited: 0 };

    // Root at the first anchor that is itself standing on passable structure.
    // An anchor buried in DECOR is a generator bug, and reporting it as
    // unreachable is exactly the right failure.
    let root = -1;
    for (let i = 0; i < anchors.length; i++) {
      const t = this.get(anchors[i].x, anchors[i].y);
      if (isPassable(t)) {
        root = i;
        break;
      }
    }
    if (root < 0) return { ok: false, unreachable: anchors.slice(), visited: 0 };

    const seen = new Set<number>();
    const queue: number[] = [];
    let head = 0;

    const rootBx = anchors[root].x >> BLOCK_SHIFT;
    const rootBy = anchors[root].y >> BLOCK_SHIFT;
    seen.add(blockKey(rootBx, rootBy));
    queue.push(rootBx, rootBy);

    while (head < queue.length) {
      const bx = queue[head++];
      const by = queue[head++];
      // 4-connected: diagonal squeezes through block corners are not routes.
      for (let d = 0; d < 4; d++) {
        const nx = bx + (d === 0 ? 1 : d === 1 ? -1 : 0);
        const ny = by + (d === 2 ? 1 : d === 3 ? -1 : 0);
        const key = blockKey(nx, ny);
        if (seen.has(key)) continue;
        if (!isPassable(this.tagAtBlock(nx, ny))) continue;
        seen.add(key);
        queue.push(nx, ny);
      }
    }

    for (let i = 0; i < anchors.length; i++) {
      const a = anchors[i];
      if (!seen.has(blockKey(a.x >> BLOCK_SHIFT, a.y >> BLOCK_SHIFT))) unreachable.push(a);
    }
    return { ok: unreachable.length === 0, unreachable, visited: seen.size };
  }

  // ------------------------------------------------- redundancy (§3.4, rev)
  //
  // Connectivity alone is NOT sufficient. Terrain destruction can only ever add
  // connectivity, but liquids can subtract it without destroying anything: lava
  // cools into obsidian and plugs a corridor, a flood makes a gallery
  // impassable, resin closes a wound. This game has Metroidvania backtracking,
  // so unlike a permadeath roguelite we cannot shrug at a self-sealed route.
  //
  // The guarantee we actually need is therefore 2-vertex-connectivity between
  // consecutive path anchors: two routes with no shared choke block. By Menger,
  // that holds exactly when both anchors sit in one biconnected component. So
  // we run Hopcroft-Tarjan once, label every EDGE with its component, and then
  // two anchors pass iff their incident-edge label sets intersect. Degree is at
  // most 4 in a 4-connected block graph, so that intersection is a handful of
  // comparisons rather than a per-pair search.

  /**
   * Check that each pair of anchors has two internally disjoint routes.
   * `pairs` defaults to consecutive entries; callers with a real path graph
   * should pass spatially adjacent pairs (see WorldGen.anchorPairs()).
   */
  verifyRedundancy(
    anchors: readonly Anchor[],
    pairs?: readonly (readonly [number, number])[],
  ): RedundancyResult {
    const weak: { a: Anchor; b: Anchor; reason: string }[] = [];
    if (anchors.length < 2) return { ok: true, checked: 0, weak };

    const use: (readonly [number, number])[] = [];
    if (pairs === undefined) {
      for (let i = 1; i < anchors.length; i++) use.push([i - 1, i]);
    } else {
      for (const p of pairs) use.push(p);
    }
    if (use.length === 0) return { ok: true, checked: 0, weak };

    const g = this.buildGraph(anchors);
    const bcc = biconnectedLabels(g);

    for (const [ia, ib] of use) {
      const a = anchors[ia];
      const b = anchors[ib];
      const na = g.index.get(blockKey(a.x >> BLOCK_SHIFT, a.y >> BLOCK_SHIFT));
      const nb = g.index.get(blockKey(b.x >> BLOCK_SHIFT, b.y >> BLOCK_SHIFT));
      if (na === undefined || nb === undefined) {
        weak.push({ a, b, reason: 'anchor not on passable structure' });
        continue;
      }
      if (!sharesComponent(bcc, na, nb)) {
        weak.push({ a, b, reason: 'single choke block on every route' });
      }
    }
    return { ok: weak.length === 0, checked: use.length, weak };
  }

  /**
   * Enumerate the passable block component reachable from the anchors into a
   * dense adjacency array. One pass, no per-node objects.
   */
  private buildGraph(anchors: readonly Anchor[]): BlockGraph {
    const index = new Map<number, number>();
    const bxs: number[] = [];
    const bys: number[] = [];
    const queue: number[] = [];
    let head = 0;

    const push = (bx: number, by: number): void => {
      const key = blockKey(bx, by);
      if (index.has(key)) return;
      if (!isPassable(this.tagAtBlock(bx, by))) return;
      index.set(key, bxs.length);
      bxs.push(bx);
      bys.push(by);
      queue.push(bx, by);
    };

    for (const a of anchors) push(a.x >> BLOCK_SHIFT, a.y >> BLOCK_SHIFT);
    while (head < queue.length) {
      const bx = queue[head++];
      const by = queue[head++];
      push(bx + 1, by);
      push(bx - 1, by);
      push(bx, by + 1);
      push(bx, by - 1);
    }

    const n = bxs.length;
    const adj = new Int32Array(n * 4).fill(-1);
    for (let i = 0; i < n; i++) {
      const bx = bxs[i];
      const by = bys[i];
      adj[i * 4 + 0] = index.get(blockKey(bx + 1, by)) ?? -1;
      adj[i * 4 + 1] = index.get(blockKey(bx - 1, by)) ?? -1;
      adj[i * 4 + 2] = index.get(blockKey(bx, by + 1)) ?? -1;
      adj[i * 4 + 3] = index.get(blockKey(bx, by - 1)) ?? -1;
    }
    return { n, adj, index };
  }

  /** Debug/telemetry: histogram of tags over all resident regions. */
  histogram(): Int32Array {
    const h = new Int32Array(STRUCT_NAMES.length);
    for (const r of this.regions.values()) {
      for (let i = 0; i < BLOCKS_PER_REGION; i++) h[r[i]]++;
    }
    return h;
  }

  clear(): void {
    this.regions.clear();
    this.cacheKey = -1;
    this.cacheRegion = null;
  }
}

/**
 * Hopcroft-Tarjan biconnected components, iterative so a 10k-block region
 * cannot blow the JS stack. Labels edges, then attributes each edge's component
 * to both its endpoints — a vertex belongs to as many components as it has
 * incident edges, and articulation points are exactly the vertices with more
 * than one.
 */
function biconnectedLabels(g: BlockGraph): BccLabels {
  const n = g.n;
  const adj = g.adj;
  const labels = new Int32Array(n * 4).fill(-1);
  const size: number[] = [];
  if (n === 0) return { labels, size };

  const disc = new Int32Array(n); // 0 = unvisited
  const low = new Int32Array(n);
  const parent = new Int32Array(n).fill(-1);
  const frameV = new Int32Array(n + 1);
  const frameI = new Int32Array(n + 1);
  const edgeA: number[] = [];
  const edgeB: number[] = [];
  let timer = 0;

  const addLabel = (v: number, id: number): void => {
    const base = v * 4;
    for (let k = 0; k < 4; k++) {
      if (labels[base + k] === id) return;
      if (labels[base + k] === -1) {
        labels[base + k] = id;
        size[id]++;
        return;
      }
    }
  };

  for (let s = 0; s < n; s++) {
    if (disc[s] !== 0) continue;
    let sp = 0;
    frameV[0] = s;
    frameI[0] = 0;
    disc[s] = low[s] = ++timer;

    while (sp >= 0) {
      const u = frameV[sp];
      if (frameI[sp] < 4) {
        const v = adj[u * 4 + frameI[sp]];
        frameI[sp]++;
        if (v < 0) continue;
        if (disc[v] === 0) {
          edgeA.push(u);
          edgeB.push(v);
          parent[v] = u;
          disc[v] = low[v] = ++timer;
          sp++;
          frameV[sp] = v;
          frameI[sp] = 0;
        } else if (v !== parent[u] && disc[v] < disc[u]) {
          edgeA.push(u);
          edgeB.push(v);
          if (disc[v] < low[u]) low[u] = disc[v];
        }
      } else {
        sp--;
        if (sp < 0) break;
        const p = frameV[sp];
        if (low[u] < low[p]) low[p] = low[u];
        if (low[u] >= disc[p]) {
          const id = size.length;
          size.push(0);
          for (;;) {
            const a = edgeA.pop();
            const b = edgeB.pop();
            if (a === undefined || b === undefined) break;
            addLabel(a, id);
            addLabel(b, id);
            if (a === p && b === u) break;
          }
        }
      }
    }
  }
  return { labels, size };
}

/**
 * True when both vertices sit in one biconnected component of at least three
 * vertices. The size test matters: a 2-vertex component is a single bridge
 * edge, which is one route, not two.
 */
function sharesComponent(bcc: BccLabels, a: number, b: number): boolean {
  const ba = a * 4;
  const bb = b * 4;
  for (let i = 0; i < 4; i++) {
    const id = bcc.labels[ba + i];
    if (id < 0) break;
    if (bcc.size[id] < 3) continue;
    for (let j = 0; j < 4; j++) {
      if (bcc.labels[bb + j] === id) return true;
    }
  }
  return false;
}
